import React, { useState } from 'react';
import { Lock, RotateCcw, Star } from 'lucide-react';
import type { RequesterTicketDetail } from '../../services/supportApi';
import ThreadComposer from './ThreadComposer';

interface RequesterPanelProps {
  ticket: RequesterTicketDetail;

  // Risposta
  replyBody: string;
  onReplyBodyChange: (value: string) => void;
  replyFiles: File[];
  onAddFiles: (picked: File[]) => void;
  onRemoveFile: (index: number) => void;
  onSend: () => void;
  isSending?: boolean;

  // Riapertura
  canReopen: boolean;
  isReopening?: boolean;
  onReopen: () => void;

  // Valutazione
  onRate: (stars: number, comment: string) => void;
  isRating?: boolean;

  /** Testi che cambiano fra associato («ticket») e ospite («richiesta»). */
  labels: { closed: string; reopen: string };
}

// Blocco lato richiedente: box risposta (o lucchetto + riapri) e valutazione.
// Condiviso fra la pagina associato e la pagina ospite: cambiano solo i testi.
const RequesterPanel: React.FC<RequesterPanelProps> = ({
  ticket,
  replyBody,
  onReplyBodyChange,
  replyFiles,
  onAddFiles,
  onRemoveFile,
  onSend,
  isSending = false,
  canReopen,
  isReopening = false,
  onReopen,
  onRate,
  isRating = false,
  labels,
}) => {
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [ratingComment, setRatingComment] = useState('');

  // 'chiuso' e definitivo: niente scrittura (il server risponde 409) e niente
  // riapertura. Da 'risolto' si puo ancora scrivere E riaprire entro la finestra.
  const isClosed = ticket.status === 'chiuso';
  const canRate = (ticket.status === 'risolto' || ticket.status === 'chiuso') && !ticket.rating;

  const reopenButton = canReopen ? (
    <button
      onClick={onReopen}
      disabled={isReopening}
      className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isReopening ? (
        <>
          <div className="w-4 h-4 border-2 border-indigo-400/40 border-t-indigo-600 rounded-full animate-spin" />
          Riapertura...
        </>
      ) : (
        <>
          <RotateCcw className="w-4 h-4" />
          {labels.reopen}
        </>
      )}
    </button>
  ) : null;

  return (
    <>
      {/* Box risposta / riapri */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
        {isClosed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 justify-center py-1">
              <Lock className="w-4 h-4" />
              <span>{labels.closed}</span>
            </div>
            {reopenButton}
          </div>
        ) : (
          <div className="space-y-3">
            <ThreadComposer
              variant="requester"
              value={replyBody}
              onChange={onReplyBodyChange}
              files={replyFiles}
              onAddFiles={onAddFiles}
              onRemoveFile={onRemoveFile}
              onSend={onSend}
              busy={isSending}
            />
            {/* Da 'risolto': si puo rispondere oppure riaprire esplicitamente. */}
            {reopenButton}
          </div>
        )}
      </div>

      {/* Valutazione */}
      {canRate && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5 space-y-3">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            Valuta l'assistenza ricevuta
          </p>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStars(s)}
                onMouseEnter={() => setHoverStars(s)}
                onMouseLeave={() => setHoverStars(0)}
                className="p-0.5"
                aria-label={`${s} stelle`}
              >
                <Star
                  className={`w-6 h-6 transition-colors ${
                    s <= (hoverStars || stars)
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300 dark:text-gray-600'
                  }`}
                />
              </button>
            ))}
          </div>
          <textarea
            value={ratingComment}
            onChange={(e) => setRatingComment(e.target.value)}
            placeholder="Lascia un commento (facoltativo)..."
            rows={3}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            maxLength={1000}
          />
          <button
            onClick={() => onRate(stars, ratingComment.trim())}
            disabled={isRating || stars < 1}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Invio...
              </>
            ) : (
              'Invia valutazione'
            )}
          </button>
        </div>
      )}

      {/* Valutazione gia inviata */}
      {ticket.rating && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
          <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
            La tua valutazione
          </p>
          <div className="flex items-center gap-1 mb-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={s}
                className={`w-4 h-4 ${
                  s <= ticket.rating!.stars
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-300 dark:text-gray-600'
                }`}
              />
            ))}
          </div>
          {ticket.rating.comment && (
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {ticket.rating.comment}
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default RequesterPanel;
